import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import Icon1 from "@assets/icons/Icon";
import VehicleImage from "@components/ui/VehicleImage";
import { navigateTo } from "context/navigation.service";
import { vehiclesApi } from "@services/api/vehicles";
import type { Vehicle, VehicleReservation } from "types/garage/vehicules";

/** Pastilles « véhicule de remplacement », visibles d'un coup d'œil sur la fiche
 *  ordre ET dans la modale ordre — avant, l'info n'existait que dans le menu
 *  Actions. La photo du véhicule porte le signal (aucune autre pastille de la
 *  ligne n'a d'image), la pastille reste neutre pour ne pas se confondre avec
 *  les chips colorées mécanique / carrosserie / montant.
 *  Le détail s'ouvre dans un portail (position fixed, z-index > BaseModal) sinon
 *  l'en-tête `overflow-hidden` de la modale le rognerait. */

export const MOBILITY_LABELS: Record<string, string> = {
  attente: "Attente sur place",
  velo: "Vélo",
  pret: "Véhicule de prêt",
  location: "Véhicule de location",
};

const USAGE_LABELS: Record<string, string> = {
  pret: "Prêt",
  location: "Location",
  velos: "Vélo",
};

function formatDay(iso?: string): string {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  if (!y || !m || !d) return iso;
  return `${d}/${m}`;
}

/** Vignette du véhicule : photo propre au véhicule si le garage en a chargé une,
 *  sinon la silhouette catalogue (marque/modèle/couleur). */
export function VehicleThumb({ vehicle, width, height }: { vehicle: Vehicle; width: number; height: number }) {
  const [broken, setBroken] = useState(false);
  if (vehicle.image_url && !broken) {
    return (
      <img
        src={vehicle.image_url}
        alt={vehicle.plate}
        style={{ width, height, objectFit: "contain", flexShrink: 0 }}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <div style={{ width, height, flexShrink: 0 }}>
      <VehicleImage
        marque={vehicle.marque}
        modele={vehicle.modele}
        coloris={vehicle.coloris}
        alt={vehicle.plate}
        iconSize={Math.round(height * 0.8)}
      />
    </div>
  );
}

export default function RentalVehicleBadge({
  vehicle,
  reservationId,
  onBeforeNavigate,
}: {
  vehicle: Vehicle;
  reservationId?: string;
  /** La modale doit se fermer avant de naviguer vers le planning véhicules. */
  onBeforeNavigate?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [reservation, setReservation] = useState<VehicleReservation | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const usageLabel = USAGE_LABELS[vehicle.usage] || "Prêt";

  // Position du panneau + fermeture au clic extérieur / Échap
  useEffect(() => {
    if (!open) return;
    const rect = btnRef.current?.getBoundingClientRect();
    if (rect) {
      const width = 280;
      setPos({
        top: Math.min(rect.bottom + 8, window.innerHeight - 260),
        left: Math.min(rect.left, window.innerWidth - width - 16),
      });
    }
    const onDown = (e: MouseEvent) => {
      if (panelRef.current?.contains(e.target as Node)) return;
      if (btnRef.current?.contains(e.target as Node)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // Période de prêt : chargée à l'ouverture seulement (une pastille par ordre,
  // inutile d'appeler l'API tant que personne ne regarde le détail).
  useEffect(() => {
    if (!open || !reservationId || reservation) return;
    vehiclesApi.getReservation(reservationId).then(setReservation).catch(() => {});
  }, [open, reservationId]);

  const period = reservation
    ? reservation.startDate === reservation.endDate
      ? formatDay(reservation.startDate)
      : `${formatDay(reservation.startDate)} → ${formatDay(reservation.endDate)}`
    : "";

  return (
    <>
      <button
        ref={btnRef}
        title={`${usageLabel} attribué : ${vehicle.name || ""} ${vehicle.plate}`.trim()}
        className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full pl-1 pr-3 py-0.5 truncate hover:bg-neutral-50 transition-colors"
        style={{ background: "#FFFFFF", color: "#404040", border: "1px solid #D4D4D4", maxWidth: 240 }}
        onClick={() => setOpen(!open)}
      >
        <VehicleThumb vehicle={vehicle} width={34} height={22} />
        <span className="truncate">{usageLabel} · {vehicle.plate}</span>
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          className="bg-white rounded-xl shadow-lg border border-neutral-200"
          style={{
            position: "fixed",
            zIndex: 10001, // au-dessus de BaseModal (z-[10000])
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            opacity: pos ? 1 : 0,
            width: 280,
            padding: 16,
          }}
        >
          <div className="flex flex-col gap-3">
            <div className="flex items-center justify-center rounded-lg" style={{ height: 90, background: "#FAFAFA" }}>
              <VehicleThumb vehicle={vehicle} width={220} height={84} />
            </div>

            <div className="flex flex-col min-w-0">
              <span className="text-h2 font-semibold truncate">{vehicle.name || vehicle.plate}</span>
              <span className="text-h4 text-neutral-400">{vehicle.plate} · {usageLabel}</span>
            </div>

            {vehicle.marque && (
              <div className="flex flex-row gap-2 text-h3 text-neutral-500">
                <span>{vehicle.marque} {vehicle.modele}</span>
                {vehicle.coloris && <span>· {vehicle.coloris}</span>}
              </div>
            )}

            {period && (
              <div className="flex flex-row items-center gap-2 text-h3 text-neutral-500">
                <Icon1 name="calendar" size={14} color="#737373" />
                <span>{period}</span>
              </div>
            )}

            {reservationId && (
              <button
                className="flex flex-row items-center justify-center gap-2 w-full py-2 rounded-lg text-h3 font-medium hover:bg-indigo-50 transition-colors"
                style={{ border: "1px solid #C7D2FE", color: "#4F46E5" }}
                onClick={() => {
                  setOpen(false);
                  onBeforeNavigate?.();
                  navigateTo({ route: `/vehicules?reservation=${reservationId}` });
                }}
              >
                <Icon1 name="calendar" size={14} color="#4F46E5" />
                Voir la réservation
              </button>
            )}
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

/** Demande de mobilité encore sans véhicule attribué : le bordereau en pointillés
 *  dit « prévu, pas encore fait » sans crier plus fort qu'un véhicule réellement
 *  attribué (qui, lui, montre sa photo). */
export function MobilityRequestChip({ mobilityOption }: { mobilityOption: string }) {
  const waiting = mobilityOption === "attente";
  const label = waiting
    ? "Attente sur place"
    : `${MOBILITY_LABELS[mobilityOption] || mobilityOption} demandé`;
  return (
    <span
      title={waiting ? "Client en attente sur place" : "Demandé au RDV — aucun véhicule attribué"}
      className="flex flex-row items-center gap-1.5 text-h3 font-medium rounded-full px-3 py-1"
      style={{
        background: "#FFFFFF",
        color: "#737373",
        border: `1px ${waiting ? "solid" : "dashed"} #D4D4D4`,
      }}
    >
      <Icon1 name={waiting ? "horloge" : "car"} size={12} color="#737373" />
      {label}
    </span>
  );
}
